import glyphMap from "@assets/nanoicons/LissenNanoIcons.glyphmap.json";
import { BoxProps, createBox } from "@shopify/restyle";
import { Theme, theme } from "@theme";
import logoIcons from "@utils/icons";
import React from "react";
import { ActivityIndicator } from "react-native";
import { createNanoIconSet } from "react-native-nano-icons";
import LBox from "./LBox";
import {
  getIconLayerColors,
  isLayeredIconName,
  type IconColors,
  type LayeredIconName,
} from "./LIcon.layers";
import LPressable from "./LPressable";

const NanoIcon = createNanoIconSet(glyphMap);

type NanoIconName = keyof typeof glyphMap.i;
type LogoIconName = keyof typeof logoIcons;

export type IconName = NanoIconName | LogoIconName;

type LayerColorProps<Name extends IconName> = Name extends LayeredIconName
  ? { colors?: IconColors<Name> }
  : { colors?: never };

export type LIconProps<Name extends IconName = IconName> = BoxProps<Theme> &
  LayerColorProps<Name> & {
    accessibilityLabel?: string;
    disabledOpacity?: number;
    enabled?: boolean;
    fadeEnabled?: boolean;
    fill?: string;
    name: Name;
    noPressablePadding?: true;
    onPress?: (() => void) | (() => Promise<void>);
    rotate?: number;
    sentryEventName?: string;
    size?: number;
    sizeHeight?: number;
    sizeWidth?: number;
    stroke?: string;
    strokeWidth?: number;
    testID?: string;
    disableLoading?: boolean;
  };

interface ContainerProps {
  accessibilityLabel?: string;
  children: React.ReactNode;
  enabled: boolean;
  fadeEnabled: boolean;
  loading: boolean;
  noPressablePadding?: true;
  onPress?: (() => void) | (() => Promise<void>);
  onPressInternal: () => void | Promise<void>;
  sentryEventName?: string;
  testID?: string;
}

const Container = ({
  accessibilityLabel,
  children,
  enabled,
  fadeEnabled,
  loading,
  noPressablePadding,
  onPress,
  onPressInternal,
  sentryEventName,
  testID,
}: ContainerProps) =>
  onPress ? (
    <LPressable
      accessibilityLabel={accessibilityLabel}
      disabled={!enabled || loading}
      fadeEnabled={fadeEnabled}
      hitSlop={12}
      onPress={onPressInternal}
      sentryEventName={sentryEventName || ""}
      style={{
        alignItems: "center",
        flex: 1,
        justifyContent: "center",
        padding: noPressablePadding ? undefined : theme.spacing.s1,
      }}
      testID={testID}
    >
      {children}
    </LPressable>
  ) : (
    <>{children}</>
  );

const isLogoIcon = (name: IconName): name is LogoIconName =>
  Object.hasOwn(logoIcons, name);

const resolveColor = (value?: string) => {
  if (!value || value === "none" || value === "transparent") return undefined;
  return value in theme.colors
    ? theme.colors[value as keyof typeof theme.colors]
    : value;
};

const Icon = ({
  accessibilityLabel,
  colors,
  disabledOpacity = 0.5,
  enabled = true,
  fadeEnabled = false,
  fill,
  name,
  noPressablePadding,
  onPress,
  rotate = 0,
  sentryEventName,
  size = 20,
  sizeHeight,
  sizeWidth,
  stroke,
  strokeWidth,
  testID,
  disableLoading = false,
  ...rest
}: LIconProps) => {
  const [loading, setLoading] = React.useState(false);

  const onPressInternal = async () => {
    if (disableLoading) return onPress?.();

    setLoading(true);
    await onPress?.();
    setLoading(false);
  };

  const iconHeight = sizeHeight ?? size;
  const color = resolveColor(stroke) ?? resolveColor(fill);
  const layerColors = isLayeredIconName(name)
    ? getIconLayerColors(name, colors as IconColors<typeof name>)
    : undefined;
  const resolvedLayerColors = layerColors?.map(
    (layerColor) => resolveColor(layerColor) ?? layerColor,
  );

  return (
    // @ts-expect-error ts type error
    <LBox {...rest} opacity={enabled ? 1 : disabledOpacity}>
      <LBox
        alignItems="center"
        height={iconHeight}
        justifyContent="center"
        width={sizeWidth ?? size}
      >
        {loading ? (
          <ActivityIndicator />
        ) : (
          <Container
            accessibilityLabel={accessibilityLabel}
            enabled={enabled}
            fadeEnabled={fadeEnabled}
            loading={loading}
            noPressablePadding={noPressablePadding}
            onPress={onPress}
            onPressInternal={onPressInternal}
            sentryEventName={sentryEventName}
            testID={testID}
          >
            {isLogoIcon(name) ? (
              React.createElement(logoIcons[name], {
                fill,
                height: "100%",
                pointerEvents: onPress ? "auto" : "none",
                stroke,
                strokeWidth,
                style: { transform: [{ rotate: `${rotate}deg` }] },
                width: "100%",
              })
            ) : (
              <NanoIcon
                accessible={false}
                color={resolvedLayerColors ?? color}
                name={name}
                size={iconHeight}
                style={{ transform: `rotate(${rotate}deg)` } as never}
              />
            )}
          </Container>
        )}
      </LBox>
    </LBox>
  );
};

const LIcon = createBox<Theme, LIconProps>(Icon);

export default LIcon as <Name extends IconName>(
  props: LIconProps<Name>,
) => React.JSX.Element;
